import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

// @mui material components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Tutorial components
import TutorialStep from "../TutorialStep";

import { AuthContext } from "context";
import AuthService from "services/auth-service";

// @mui icons
// @mui icons
import InsightsIcon from "@mui/icons-material/Insights";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Assets
import comparedlyLogo from "assets/images/logos/comparedly.png";

function LiveDemoIntroStep({ open, onComplete, onSkip }) {
    const navigate = useNavigate();

    const handleStartExploring = () => {
        onComplete();
        navigate("/social-stash");
    };

    const dialogContent = (
        <MDBox textAlign="center" px={2} pb={2}>
            {/* Brand Logo */}
            <MDBox
                mx="auto"
                width="80px"
                height="80px"
                mb={3}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <MDBox component="img" src={comparedlyLogo} alt="Comparedly" width="100%" height="auto" />
            </MDBox>

            <MDTypography variant="h4" fontWeight="bold" color="dark" gutterBottom>
                Welcome to the Live Demo
            </MDTypography>

            <MDTypography variant="body2" color="text" sx={{ mb: 2, maxWidth: "80%", mx: "auto", opacity: 0.8 }}>
                Experience the power of Comparedly as a guest user with live public data.
            </MDTypography>

            <MDBox
                display="inline-block"
                bgColor="rgba(0, 161, 161, 0.1)"
                color="info"
                borderRadius="xl"
                px={2}
                py={0.5}
                mb={4}
                border="1px solid rgba(0, 161, 161, 0.3)"
            >
                <MDTypography variant="caption" color="inherit" fontWeight="bold">
                    🛡️ Explore safely — no login, no client accounts, public data only
                </MDTypography>
            </MDBox>

            {/* Features Dotted Box */}
            <MDBox
                sx={{
                    border: "2px dashed #e0e0e0",
                    borderRadius: "16px",
                    padding: "24px",
                    backgroundColor: "#fcfcfc",
                    mb: 2,
                    mx: "auto",
                    width: "100%",
                    maxWidth: "500px"
                }}
            >
                {/* Intro Text */}
                <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} mb={3}>
                    <CheckCircleIcon sx={{ color: "#00A1A1", fontSize: "1.2rem" }} />
                    <MDTypography variant="subtitle2" color="text" fontWeight="medium" textAlign="center">
                        See these features in action with our sample profiles:
                    </MDTypography>
                </MDBox>

                {/* Features List */}
                <MDBox display="flex" flexDirection="column" gap={1.5}>
                    {[
                        {
                            icon: <InsightsIcon />,
                            color: "info",
                            title: "Instant Insights",
                            description: "Audience, Engagement, Content & Sentiment Analysis"
                        },
                        {
                            icon: <CompareArrowsIcon />,
                            color: "error", // Reddish for "Powerful Comparisons"
                            title: "Powerful Comparisons",
                            description: "Compare multiple profiles across & within platforms"
                        },
                        {
                            icon: <AutoAwesomeIcon />,
                            color: "warning",
                            title: "Ask AI",
                            description: "Get AI-powered answers about your data"
                        }
                    ].map((feature, index) => (
                        <MDBox
                            key={index}
                            display="flex"
                            alignItems="center"
                            p={1.5}
                            borderRadius="lg"
                            border="1px solid #e0e0e0"
                            bgColor="white"
                            width="100%"
                        >
                            {/* Left Icon */}
                            <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                width="2.5rem"
                                height="2.5rem"
                                borderRadius="lg"
                                mr={2}
                                sx={{
                                    backgroundColor: ({ palette: { [feature.color]: color }, functions: { rgba } }) => rgba(color.main, 0.1),
                                    color: ({ palette: { [feature.color]: color } }) => color.main,
                                }}
                            >
                                {/* Clone element to ensure size/props inherited if needed, though Icon wrapper handles most */}
                                {React.cloneElement(feature.icon, { fontSize: "medium" })}
                            </MDBox>

                            {/* Right Text */}
                            <MDBox display="flex" flexDirection="column" alignItems="flex-start" textAlign="left">
                                <MDTypography variant="subtitle2" fontWeight="bold" color="text">
                                    {feature.title}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, lineHeight: 1.2 }}>
                                    {feature.description}
                                </MDTypography>
                            </MDBox>

                        </MDBox>
                    ))}
                </MDBox>
            </MDBox>
        </MDBox>
    );

    const authContext = useContext(AuthContext);

    const handleExit = async () => {
        // Clear demo-specific session storage
        sessionStorage.removeItem('demo_role_cache');
        sessionStorage.removeItem('demo_completed_tutorials');

        // Logout from AuthContext and Service
        await AuthService.logout();
        authContext.logout();

        // Redirect to Landing Page
        window.location.href = process.env.REACT_APP_URL_LANDING || "https://comparedly.com";
    };

    const dialogActions = (
        <MDBox width="100%" display="flex" justifyContent="space-between" alignItems="center" px={2} pb={1}>
            <MDButton
                variant="text"
                color="secondary"
                onClick={handleExit}
            >
                Exit Live Demo
            </MDButton>
            <MDButton
                variant="gradient"
                color="accent"
                size="large"
                onClick={handleStartExploring}
                sx={{ minWidth: 160, boxShadow: "0 4px 14px rgba(0,0,0,0.1)" }}
            >
                Start Exploring
            </MDButton>
        </MDBox>
    );

    const handleClose = (event, reason) => {
        if (reason === "backdropClick") return;
        onSkip();
    };

    return (
        <TutorialStep
            open={open}
            // title="Live Demo Access" // Removing title to let the custom header take focus
            content={dialogContent}
            actions={dialogActions}
            onClose={handleClose}
        />
    );
}

LiveDemoIntroStep.propTypes = {
    open: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
};

export default LiveDemoIntroStep;
